import { PRICE_TIER_REQUIRED_ERROR, UNASSIGNED_LABEL } from "@/constants/adminCollections";
import type {
  AdminCollectionProduct,
  AssignFormValue,
  AssignSubmit,
  CustomProductAssignment,
  OrganizationOption,
} from "@/interfaces/adminCollections";

const EMPTY_VALUE = "—";

const usdFormats = {
  whole: new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }),
  cents: new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }),
};

/** The one USD formatter: whole dollars by default ("$1,000"), or "$49.99" with `cents`. */
export function formatUsd(value: number, { cents = false }: { cents?: boolean } = {}): string {
  return usdFormats[cents ? "cents" : "whole"].format(value);
}

const syncedDateFormat = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

/** null (the custom collection) -> "Custom"; 1000 -> "$1,000". */
export function formatCollectionValue(value: number | null): string {
  return value === null ? "Custom" : formatUsd(value);
}

/** ISO timestamp -> "Jun 15, 2026" in UTC; null -> "—". */
export function formatSyncedDate(iso: string | null): string {
  return iso === null ? EMPTY_VALUE : syncedDateFormat.format(new Date(iso));
}

export function formatProductCount(n: number): string {
  return `${n} ${n === 1 ? "product" : "products"}`;
}

/** "<org> · $<tier>" */
export function formatAssignmentChip(a: CustomProductAssignment): string {
  return `${a.organizationName} · ${formatUsd(a.priceTier)}`;
}

/** "Unassigned" first, then each organization (value coerced to a number) in input order. */
export function buildOrganizationOptions(
  filterOptions: readonly { value: string; label: string }[],
): OrganizationOption[] {
  return [
    { value: null, label: UNASSIGNED_LABEL },
    ...filterOptions.map((o) => ({ value: Number(o.value), label: o.label })),
  ];
}

/** Decides what Save does for the form against the product's current assignment. */
export function resolveAssignSubmit(
  current: CustomProductAssignment | null,
  form: AssignFormValue,
): AssignSubmit {
  if (form.organizationId === null) {
    return current === null ? { kind: "noop" } : { kind: "unassign" };
  }
  if (form.priceTier === null) return { kind: "invalid", error: PRICE_TIER_REQUIRED_ERROR };
  if (
    current !== null &&
    current.organizationId === form.organizationId &&
    current.priceTier === form.priceTier
  ) {
    return { kind: "noop" };
  }
  return { kind: "assign", organizationId: form.organizationId, priceTier: form.priceTier };
}

/** The assign form's starting value: the current assignment, else the Shopify-price tier suggestion. */
export function initialAssignForm(product: AdminCollectionProduct): AssignFormValue {
  const current = product.assignment;
  if (current) return { organizationId: current.organizationId, priceTier: current.priceTier };
  return { organizationId: null, priceTier: product.suggestedPriceTier };
}
